import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import * as XLSX from 'xlsx'
import { Grid, GridColumn } from '@progress/kendo-react-grid'
import { orderBy, filterBy } from '@progress/kendo-data-query'
import { DatePicker } from '@progress/kendo-react-dateinputs'
import { Button } from '@progress/kendo-react-buttons'
import { FaFileExcel, FaSearch } from 'react-icons/fa'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts'
import { getCurrentUserEmployee } from '../../data/employeeData'
import { appendEmployeeIds } from '../../data/reportUserScopeData'
import ReportUserFilter, { useReportUserFilter } from '../../components/ReportUserFilter/ReportUserFilter'
import { useToast } from '../../contexts/ToastContext'
import './QuoteConversionReport.css'

interface QuoteConversionRow {
  employeeId: number
  name: string
  totalQuotes: number
  quotesWon: number
  conversionRatePct: number
  totalQuoteValue: number
  wonQuoteValue: number
  valueConversionRatePct: number
}

const formatCurrency = (value: number) =>
  value.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })

function QuoteConversionReport() {
  const navigate = useNavigate()
  const { showToast } = useToast()

  const [startDate, setStartDate] = useState<Date | null>(new Date(new Date().getFullYear(), 0, 1))
  const [endDate, setEndDate] = useState<Date | null>(new Date())
  const [data, setData] = useState<QuoteConversionRow[]>([])
  const [loading, setLoading] = useState(false)
  const [hasPermission, setHasPermission] = useState(false)
  const users = useReportUserFilter(hasPermission)

  const [sort, setSort] = useState<any[]>([])
  const [filter, setFilter] = useState<any>(null)
  const [skip, setSkip] = useState(0)
  const [pageSize] = useState(25)

  const apiURL = process.env.REACT_APP_API_URL

  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const currentUser = await getCurrentUserEmployee()
        if (currentUser && currentUser.permissions) {
          const permitted = currentUser.permissions.some(
            (p) => p.area === 'Reports' && p.permission === 'QuoteConversion' && p.assigned
          )
          setHasPermission(permitted)
          if (!permitted) {
            showToast('You do not have permission to access this page', 'error')
            navigate('/')
          }
        } else {
          showToast('Unable to verify permissions', 'error')
          navigate('/')
        }
      } catch (error) {
        console.error('Error checking permissions:', error)
        showToast('Error verifying permissions', 'error')
        navigate('/')
      }
    }
    checkPermissions()
  }, [navigate, showToast])

  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) {
      showToast('Please select both start and end dates', 'info')
      return
    }
    if (startDate > endDate) {
      showToast('Start date must be before end date', 'error')
      return
    }
    try {
      setLoading(true)
      const token = localStorage.getItem('authToken')
      if (!token) throw new Error('Authorization token not found')

      const startStr = startDate.toISOString().split('T')[0]
      const endStr = endDate.toISOString().split('T')[0]
      const query = appendEmployeeIds(
        new URLSearchParams({ startDate: startStr, endDate: endStr }),
        users.selectedIds
      )
      const url = `https://${apiURL}/Quotes/ConversionReport?${query}`

      const response = await axios.get<QuoteConversionRow[]>(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token,
        },
      })
      setData(response.data)
      showToast(`Loaded conversion data for ${response.data.length} employees`, 'success')
    } catch (error: any) {
      console.error('Error fetching quote conversion data:', error)
      const msg = error.response?.data?.message || error.message || 'Error loading quote conversion data'
      showToast(msg, 'error')
    } finally {
      setLoading(false)
    }
  }, [startDate, endDate, users.selectedIds, showToast, apiURL])

  const dataFiltered = filter ? filterBy(data, filter) : data
  const dataSorted = orderBy(dataFiltered, sort)

  const totalQuotes = dataSorted.reduce((sum, row) => sum + row.totalQuotes, 0)
  const totalWon = dataSorted.reduce((sum, row) => sum + row.quotesWon, 0)
  const totalValue = dataSorted.reduce((sum, row) => sum + row.totalQuoteValue, 0)
  const totalWonValue = dataSorted.reduce((sum, row) => sum + row.wonQuoteValue, 0)
  const overallConversionPct = totalQuotes > 0 ? (100 * totalWon) / totalQuotes : 0
  const overallValueConversionPct = totalValue > 0 ? (100 * totalWonValue) / totalValue : 0

  const exportToExcel = () => {
    const exportData = dataSorted.map((row) => ({
      'Name': row.name,
      'Total Quotes': row.totalQuotes,
      'Quotes Won': row.quotesWon,
      'Conversion Rate (%)': row.conversionRatePct,
      'Total Quote Value': Number(row.totalQuoteValue),
      'Won Quote Value': Number(row.wonQuoteValue),
      'Value Conversion Rate (%)': row.valueConversionRatePct,
    }))

    const worksheet = XLSX.utils.json_to_sheet(exportData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Quote Conversion')
    const fileName = `quote_conversion_report_${startDate?.toISOString().split('T')[0]}_to_${endDate?.toISOString().split('T')[0]}.xlsx`
    XLSX.writeFile(workbook, fileName)
    showToast(`Exported ${exportData.length} rows to ${fileName}`, 'success')
  }

  const axisTick = { fontSize: '9pt', fill: 'rgb(30, 42, 78)' }

  return (
    <div>
      <div style={{ padding: '1rem' }}>
        <div className="quote-conversion-report__header">
          <h1>Quote Conversion Report</h1>
        </div>

        {/* Filters */}
        <div style={{
          background: '#f5f9fb',
          padding: '1.5rem',
          borderRadius: '8px',
          marginBottom: '1rem',
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            alignItems: 'end',
          }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#1e2a4e' }}>
                Start Date:
              </label>
              <DatePicker
                value={startDate}
                onChange={(e) => setStartDate(e.value)}
                format="dd/MM/yyyy"
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#1e2a4e' }}>
                End Date:
              </label>
              <DatePicker
                value={endDate}
                onChange={(e) => setEndDate(e.value)}
                format="dd/MM/yyyy"
                style={{ width: '100%' }}
              />
            </div>
            <ReportUserFilter
              scope={users.scope}
              value={users.selectedIds}
              onChange={users.setSelectedIds}
              disabled={loading}
              label="Users:"
              labelStyle={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#1e2a4e' }}
              width="100%"
            />
            <div>
              <Button
                onClick={fetchData}
                themeColor="primary"
                disabled={loading || !hasPermission}
                style={{ width: '100%', height: '40px' }}
              >
                <FaSearch style={{ marginRight: '0.5rem' }} />
                {loading ? 'Loading...' : 'Run Report'}
              </Button>
            </div>
          </div>
        </div>

        {/* Summary + Export */}
        {data.length > 0 && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
              <Button onClick={exportToExcel} themeColor="primary" data-excel="true">
                <FaFileExcel style={{ marginRight: '0.5rem' }} />
                Export to Excel
              </Button>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '1rem',
              marginBottom: '1.5rem',
            }}>
              <div style={{
                background: '#1e2a4e', color: 'white', padding: '1rem',
                borderRadius: '8px', textAlign: 'center',
              }}>
                <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Total Quotes</div>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>{totalQuotes}</div>
              </div>
              <div style={{
                background: '#aec741', color: '#475315', padding: '1rem',
                borderRadius: '8px', textAlign: 'center',
              }}>
                <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Quotes Won</div>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>{totalWon}</div>
              </div>
              <div style={{
                background: '#e9540d', color: 'white', padding: '1rem',
                borderRadius: '8px', textAlign: 'center',
              }}>
                <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Conversion Rate</div>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>{overallConversionPct.toFixed(1)}%</div>
              </div>
              <div style={{
                background: '#1e2a4e', color: 'white', padding: '1rem',
                borderRadius: '8px', textAlign: 'center',
              }}>
                <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Total Quote Value</div>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>{formatCurrency(totalValue)}</div>
              </div>
              <div style={{
                background: '#aec741', color: '#475315', padding: '1rem',
                borderRadius: '8px', textAlign: 'center',
              }}>
                <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Won Quote Value</div>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>{formatCurrency(totalWonValue)}</div>
              </div>
              <div style={{
                background: '#e9540d', color: 'white', padding: '1rem',
                borderRadius: '8px', textAlign: 'center',
              }}>
                <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Value Conversion Rate</div>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>{overallValueConversionPct.toFixed(1)}%</div>
              </div>
            </div>
          </div>
        )}

        {/* Charts */}
        {data.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
            gap: '1rem',
            marginBottom: '1.5rem',
          }}>
            <div className="quote-conversion-report__chart">
              <h3>Quotes vs Won by Employee</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dataSorted}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={axisTick} interval={0} angle={-30} textAnchor="end" height={70} />
                  <YAxis tick={axisTick} allowDecimals={false} />
                  <Tooltip />
                  <Legend verticalAlign="top" />
                  <Bar dataKey="totalQuotes" name="Total Quotes" fill="#1e2a4e" />
                  <Bar dataKey="quotesWon" name="Quotes Won" fill="#aec741" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="quote-conversion-report__chart">
              <h3>Conversion Rates by Employee (%)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dataSorted}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={axisTick} interval={0} angle={-30} textAnchor="end" height={70} />
                  <YAxis tick={axisTick} domain={[0, 100]} unit="%" />
                  <Tooltip formatter={(value: any) => `${value}%`} />
                  <Legend verticalAlign="top" />
                  <Bar dataKey="conversionRatePct" name="Conversion Rate" fill="#e9540d" />
                  <Bar dataKey="valueConversionRatePct" name="Value Conversion Rate" fill="#1e2a4e" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="quote-conversion-report__chart">
              <h3>Quote Value vs Won Value by Employee</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dataSorted}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={axisTick} interval={0} angle={-30} textAnchor="end" height={70} />
                  <YAxis tick={axisTick} tickFormatter={(value: number) => formatCurrency(value)} width={90} />
                  <Tooltip formatter={(value: any) => formatCurrency(Number(value))} />
                  <Legend verticalAlign="top" />
                  <Bar dataKey="totalQuoteValue" name="Total Quote Value" fill="#1e2a4e" />
                  <Bar dataKey="wonQuoteValue" name="Won Quote Value" fill="#aec741" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Grid */}
        {data.length > 0 && (
          <div className="quote-conversion-report-grid">
            <Grid
              data={dataSorted.slice(skip, skip + pageSize)}
              sortable
              sort={sort}
              onSortChange={(e) => setSort(e.sort)}
              filterable
              filter={filter}
              onFilterChange={(e) => {
                setFilter(e.filter)
                setSkip(0)
              }}
              pageable
              pageSize={pageSize}
              skip={skip}
              onPageChange={(e) => setSkip(e.page.skip)}
              total={dataFiltered.length}
              style={{ width: '100%' }}
            >
              <GridColumn field="name" title="Name" width="200px" />
              <GridColumn field="totalQuotes" title="Total Quotes" width="130px" filter="numeric" />
              <GridColumn field="quotesWon" title="Quotes Won" width="130px" filter="numeric" />
              <GridColumn
                field="conversionRatePct"
                title="Conversion Rate"
                width="150px"
                filter="numeric"
                cell={(props) => (
                  <td style={{ fontWeight: 'bold' }}>{props.dataItem.conversionRatePct.toFixed(2)}%</td>
                )}
              />
              <GridColumn
                field="totalQuoteValue"
                title="Total Quote Value"
                width="160px"
                filter="numeric"
                cell={(props) => <td>{formatCurrency(props.dataItem.totalQuoteValue)}</td>}
              />
              <GridColumn
                field="wonQuoteValue"
                title="Won Quote Value"
                width="160px"
                filter="numeric"
                cell={(props) => <td>{formatCurrency(props.dataItem.wonQuoteValue)}</td>}
              />
              <GridColumn
                field="valueConversionRatePct"
                title="Value Conversion Rate"
                width="180px"
                filter="numeric"
                cell={(props) => (
                  <td style={{ fontWeight: 'bold' }}>{props.dataItem.valueConversionRatePct.toFixed(2)}%</td>
                )}
              />
            </Grid>
          </div>
        )}

        {/* Empty state */}
        {!loading && data.length === 0 && (
          <div style={{ textAlign: 'center', padding: '4rem 2rem', color: '#666' }}>
            <p style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>No quote data found</p>
            <p>Select a date range and click "Run Report" to view quote conversion rates by employee</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default QuoteConversionReport
